import { cloudStorage } from '@telegram-apps/sdk-react';

export type StorageAPI = {
  readData<T>(key: string): T | null | Promise<T | null>;
  writeData<T>(key: string, data: T): void;
  removeData(key: string): void;
};

export const useLocalStorage = (): StorageAPI => {
  function readData<T>(STORAGE_KEY: string) {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  function writeData<T>(STORAGE_KEY: string, data: T) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  function removeData(STORAGE_KEY: string) {
    if (typeof window !== 'undefined') localStorage.removeItem(STORAGE_KEY);
  }

  return { readData, writeData, removeData };
};

export const useSessionStorage = (): StorageAPI => {
  function readData<T>(STORAGE_KEY: string) {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  function writeData<T>(STORAGE_KEY: string, data: T) {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  function removeData(STORAGE_KEY: string) {
    if (typeof window !== 'undefined') sessionStorage.removeItem(STORAGE_KEY);
  }

  return { readData, writeData, removeData };
};

export const useCloudStorage = (): StorageAPI => {
  async function readData<T>(STORAGE_KEY: string) {
    if (!cloudStorage.getItem.isAvailable()) return null;
    const raw = await cloudStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  async function writeData<T>(STORAGE_KEY: string, data: T) {
    if (!cloudStorage.setItem.isAvailable()) return;
    await cloudStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  async function removeData(STORAGE_KEY: string) {
    if (cloudStorage.deleteItem.isAvailable())
      await cloudStorage.deleteItem(STORAGE_KEY);
  }

  return { readData, writeData, removeData };
};
